import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getHostPayouts, getHostProfile, initStripeConnect } from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { DollarSign, ArrowRight, Loader2, CreditCard, Check } from 'lucide-react';

export default function HostPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const loadData = async (cursor = null) => {
    try {
      const [payoutsRes, profileRes] = await Promise.all([
        getHostPayouts({ limit: 20, cursor }),
        getHostProfile()
      ]);
      
      if (cursor) {
        setPayouts(prev => [...prev, ...payoutsRes.data.items]);
      } else {
        setPayouts(payoutsRes.data.items || []);
      }
      
      setProfile(profileRes.data);
      setNextCursor(payoutsRes.data.next_cursor);
      setHasMore(payoutsRes.data.has_more);
    } catch (error) {
      console.error('Failed to load payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const response = await initStripeConnect();
      setProfile(prev => ({ ...prev, payout_enabled: true, stripe_account_id: response.data.account_id }));
      toast.success('Stripe account connected successfully!');
    } catch (error) {
      toast.error('Failed to connect Stripe account');
    } finally {
      setConnecting(false);
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
  };

  const totalEarnings = payouts.reduce((sum, p) => sum + p.payout_amount, 0);
  const pendingPayouts = payouts.filter(p => p.status === 'pending');
  const pendingAmount = pendingPayouts.reduce((sum, p) => sum + p.payout_amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="host-payouts">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Payouts</h1>
        <p className="text-slate-500 mt-1">Track your earnings and payout history</p>
      </div>

      {/* Stripe Connection Status */}
      {!profile?.payout_enabled && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">Connect Your Payout Account</h3>
                  <p className="text-sm text-slate-600">
                    Connect your Stripe account to receive payouts from your bookings.
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleConnectStripe}
                disabled={connecting}
                className="btn-primary"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Connect Stripe
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {profile?.payout_enabled && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900">Payout Account Connected</h3>
                <p className="text-sm text-slate-600">
                  Your earnings will be automatically transferred to your connected account.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Earnings</p>
                <p className="text-3xl font-bold text-slate-900">{formatPrice(totalEarnings)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Payouts</p>
                <p className="text-3xl font-bold text-slate-900">{formatPrice(pendingAmount)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completed Payouts</p>
                <p className="text-3xl font-bold text-slate-900">
                  {payouts.filter(p => p.status === 'completed').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Check className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payout History */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Payout History</CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length > 0 ? (
            <div className="space-y-4">
              {payouts.map((payout) => (
                <div 
                  key={payout.booking_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900">
                      Booking #{payout.booking_id.slice(0, 8)}
                    </p>
                    <p className="text-sm text-slate-500">
                      {format(new Date(payout.check_in), 'MMM d')} - {format(new Date(payout.check_out), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge className={payout.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                      {payout.status}
                    </Badge>
                    <p className="text-lg font-semibold text-slate-900 mt-1">
                      {formatPrice(payout.payout_amount)}
                    </p>
                    <p className="text-xs text-slate-500">
                      Fee: {formatPrice(payout.host_fee)}
                    </p>
                  </div>
                </div>
              ))}

              {hasMore && (
                <div className="text-center pt-4">
                  <Button 
                    variant="outline"
                    onClick={() => loadData(nextCursor)}
                  >
                    Load More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No payouts yet. Your earnings will appear here.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
